import { useEffect } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import useGetMoneyPoolManagement from '../fundPage/hooks/useGetMoneyPoolManagement'
import posthog from 'posthog-js'
import useGetUserInfo from './hooks/useGetUserInfo'
import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import UserInfo from 'modules/financialHistory/userInfo'
import CustomTabs from 'modules/financialHistory/tabs'

export default function FinancialHistory() {
    const { moneyPool, poolship } = useParams()
    // ?legacy=1 — کارنامه bounced us back because the backend refused
    // it. Set only by MemberRecord, never by a link a person can tap.
    // Without it the two screens would redirect to each other forever.
    const [search] = useSearchParams()
    const forcedLegacy = search.get('legacy') === '1'
    const { moneyPoolManagement, isLoadingMoneypoolManagement } =
        useGetMoneyPoolManagement(moneyPool)
    const role = moneyPoolManagement?.poolship?.role
    const isManager = role === 'mgr' || role === 'own'
    // `moneyPoolManagement.poolship` is the REQUESTING user's own poolship in
    // this fund, filled server-side — the only trustworthy answer to «is this
    // page about me». Not localStorage `profile.id`, which is a MEMBER id and
    // would silently compare against a poolship id; not the fund page's
    // `poolShipId`, which is scanned out of whichever pages of the members
    // list happen to be loaded and is undefined on a big fund.
    const isSelf =
        !!moneyPoolManagement?.poolship?.id &&
        String(moneyPoolManagement.poolship.id) === String(poolship)
    const { userInfo, isLoadingUserInfo } = useGetUserInfo()
    useEffect(() => {
        posthog.capture('member_detail_viewed', {
            fund_id: moneyPool,
            poolship_id: poolship,
        })
    }, [moneyPool, poolship])
    // pwa#36: «در حال حاضر پروفایل کاربر رو توی تب صندوق‌ها مدیر همونطوری
    // می‌بینه که بقیه می‌بینن. نباید اینطور باشه.» A manager arriving here from
    // any of the old links — the fund page, the followup modal, a bookmark —
    // lands on کارنامهٔ عضو instead.
    //
    // pwa#61: «الان مدیر می‌بینه کارنامه رو ولی خودش نمی‌بینه» — so does
    // the member, about themselves. #36 left them here on the reasoning that
    // this screen "is their own record and was never the thing being
    // complained about"; the owner has since said the opposite, and the
    // member is exactly the person for whom نظم حق عضویت, سرمایه در طول
    // زمان and امتیاز are worth reading.
    //
    // The condition is self-OR-manager, never plain membership: the اعضا tab
    // is ungated, so any member can open any other member's poolship here.
    // Redirecting on membership alone would hand them a page the backend
    // 403s (and rightly).
    //
    // `replace` so the phone's back button leaves the fund rather than
    // bouncing between the two screens.
    //
    // The two apps deploy independently and a service worker can serve
    // this bundle against a backend that predates it, so the redirect
    // must not be a one-way door: when کارنامه cannot load, it sends the
    // reader back here with ?legacy=1 and this screen answers instead of
    // bouncing them again.
    if (!forcedLegacy && (isManager || isSelf)) {
        return (
            <Navigate
                replace
                to={`/fund/member-record/${moneyPool}/${poolship}`}
            />
        )
    }

    // Deciding before the role is known would flash the old screen at a
    // manager for one frame on every open.
    if (isLoadingMoneypoolManagement) {
        return (
            <div className=" bg-white min-h-screen">
                <Header title="سابقه مالی عضو" />
                <DotsLoading />
            </div>
        )
    }

    return (
        <div className=" bg-white min-h-screen">
            <Header title="سابقه مالی عضو" />
            {isLoadingUserInfo ? (
                <DotsLoading />
            ) : (
                userInfo && <UserInfo data={userInfo} />
            )}
            <CustomTabs />
        </div>
    )
}
